import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Autocomplete,
  Box,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import { gradArray } from "../../../components/form/fields/inputFields";
import { useGetSitesQuery } from "../../../features/sites/sitesApiSlice";
import { useGetMembersQuery } from "../../../features/members/membersApiSlice";
import { Controller } from "react-hook-form";

const normalizeNumericValue = (value) =>
  value === null || value === undefined || value === "" ? null : Number(value);

function SurveyTargetingFields({ control, register, watch }) {
  const { data: sites = [] } = useGetSitesQuery();
  const { data: members = [] } = useGetMembersQuery();

  const targetingEnabled = watch("targeting.enabled");
  const minDegree = normalizeNumericValue(watch("targeting.minDegree"));
  const maxDegree = normalizeNumericValue(watch("targeting.maxDegree"));
  const minAge = normalizeNumericValue(watch("targeting.minAge"));
  const maxAge = normalizeNumericValue(watch("targeting.maxAge"));

  const hasDegreeRangeWarning =
    minDegree !== null && maxDegree !== null && minDegree > maxDegree;
  const hasAgeRangeWarning =
    minAge !== null && maxAge !== null && minAge > maxAge;

  const siteOptions = useMemo(
    () =>
      sites.map((site) => ({
        id: site._id,
        label: site.siteName || "Unbenannter Standort",
      })),
    [sites],
  );

  const memberOptions = useMemo(
    () =>
      members.map((member) => {
        const fullName =
          `${member?.firstName || ""} ${member?.lastName || ""}`.trim() ||
          "Unbekanntes Mitglied";
        const siteName = member?.standortRef?.siteName
          ? ` - ${member.standortRef.siteName}`
          : "";

        return {
          id: member._id,
          label: `${fullName}${siteName}`,
        };
      }),
    [members],
  );

  return (
    <Grid item xs={12}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          background:
            "linear-gradient(140deg, rgba(255, 248, 240, 0.55) 0%, rgba(244, 252, 255, 0.65) 100%)",
        }}
      >
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Zielgruppe (optional)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Standard ist weiterhin öffentlich per Link. Hier können Sie zusätzlich
          steuern, welche Mitglieder diese Befragung direkt im Dashboard sehen.
        </Typography>

        <Controller
          name="targeting.enabled"
          control={control}
          defaultValue={false}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(field.value)}
                  onChange={(_, checked) => field.onChange(checked)}
                />
              }
              label="Mitglieder-Zielgruppe aktivieren"
            />
          )}
        />

        {targetingEnabled && (
          <Box sx={{ mt: 1 }}>
            {(hasDegreeRangeWarning || hasAgeRangeWarning) && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                Bitte prüfen Sie die Grenzen: Mindestwert darf nicht größer als
                der Maximalwert sein.
              </Alert>
            )}

            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="targeting.siteIds"
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      options={siteOptions}
                      value={siteOptions.filter((site) =>
                        Array.isArray(field.value)
                          ? field.value.includes(site.id)
                          : false,
                      )}
                      onChange={(_, selected) =>
                        field.onChange(selected.map((item) => item.id))
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Standorte"
                          placeholder="Standorte auswählen"
                          helperText="Optional: nur Mitglieder aus diesen Standorten"
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="targeting.memberIds"
                  control={control}
                  defaultValue={[]}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      options={memberOptions}
                      value={memberOptions.filter((member) =>
                        Array.isArray(field.value)
                          ? field.value.includes(member.id)
                          : false,
                      )}
                      onChange={(_, selected) =>
                        field.onChange(selected.map((item) => item.id))
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      getOptionLabel={(option) => option.label}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Explizite Mitglieder"
                          placeholder="Mitglieder auswählen"
                          helperText="Optional: direkte Freigabe für ausgewählte Mitglieder"
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name="targeting.minDegree"
                  control={control}
                  defaultValue={null}
                  render={({ field }) => (
                    <TextField
                      select
                      fullWidth
                      label="Grad von"
                      value={field.value ?? ""}
                      onChange={(event) => {
                        const value = event.target.value;
                        field.onChange(value === "" ? null : Number(value));
                      }}
                    >
                      <MenuItem value="">Kein Minimum</MenuItem>
                      {gradArray.map((degree) => (
                        <MenuItem
                          key={`min-degree-${degree.value}`}
                          value={degree.value}
                        >
                          {degree.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Controller
                  name="targeting.maxDegree"
                  control={control}
                  defaultValue={null}
                  render={({ field }) => (
                    <TextField
                      select
                      fullWidth
                      label="Grad bis"
                      value={field.value ?? ""}
                      onChange={(event) => {
                        const value = event.target.value;
                        field.onChange(value === "" ? null : Number(value));
                      }}
                    >
                      <MenuItem value="">Kein Maximum</MenuItem>
                      {gradArray.map((degree) => (
                        <MenuItem
                          key={`max-degree-${degree.value}`}
                          value={degree.value}
                        >
                          {degree.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Alter von"
                  type="number"
                  inputProps={{ min: 0, max: 120 }}
                  {...register("targeting.minAge", {
                    setValueAs: (value) =>
                      value === "" || value === null || value === undefined
                        ? null
                        : Number(value),
                  })}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Alter bis"
                  type="number"
                  inputProps={{ min: 0, max: 120 }}
                  {...register("targeting.maxAge", {
                    setValueAs: (value) =>
                      value === "" || value === null || value === undefined
                        ? null
                        : Number(value),
                  })}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>
    </Grid>
  );
}

SurveyTargetingFields.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
};

export default SurveyTargetingFields;
